import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, User, Lock, Phone, FileText, MapPin, ChevronRight, ChevronLeft } from "lucide-react";
import API from "../../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const COUNTRY_CODES = [
  { code: "+57",  flag: "🇨🇴", name: "Colombia",  digits: 10 },
  { code: "+58",  flag: "🇻🇪", name: "Venezuela", digits: 10 },
  { code: "+52",  flag: "🇲🇽", name: "México",    digits: 10 },
  { code: "+1",   flag: "🇺🇸", name: "EE.UU.",    digits: 10 },
];

export default function RegisterDelivery() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", phoneCountryCode: "+57",
    companyName: "", companyNit: "", companyPhone: "", companyAddress: "", companyCity: "", companyDescription: "",
  });

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.companyName) {
      toast.warning("Completa los campos obligatorios");
      return;
    }
    setLoading(true);
    try {
      const res = await API.post("/auth/register-delivery", {
        name:             form.name,
        email:            form.email,
        password:         form.password,
        phone:            form.phone || undefined,
        phoneCountryCode: form.phoneCountryCode,
        companyName:      form.companyName,
        companyNit:       form.companyNit       || undefined,
        companyPhone:     form.companyPhone     || undefined,
        companyAddress:   form.companyAddress   || undefined,
        companyCity:      form.companyCity      || undefined,
        companyDescription: form.companyDescription || undefined,
      });
      const data = res.data?.data ?? res.data;
      if (data?.tokens?.accessToken) {
        localStorage.setItem("token",   data.tokens.accessToken);
        localStorage.setItem("user",    JSON.stringify({ ...data.user, deliveryCompany: data.company }));
        toast.success("¡Empresa de domicilio creada exitosamente!");
        nav("/delivery");
      } else {
        toast.success("Registrado. Inicia sesión.");
        nav("/login");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al registrar");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-violet-100 flex items-center justify-center px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl w-full max-w-lg p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Building2 size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-900">Empresa de domicilio</h2>
            <p className="text-xs text-gray-500">Registro de empresa de entrega</p>
          </div>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2].map(s => (
            <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${step >= s ? "bg-indigo-600" : "bg-gray-200"}`} />
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Datos del administrador</p>

              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input type="text" placeholder="Nombre completo *" value={form.name} onChange={set("name")} required
                  className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
              </div>

              <input type="email" placeholder="Correo electrónico *" value={form.email} onChange={set("email")} required
                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />

              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input type="password" placeholder="Contraseña (mín. 6 caracteres) *" value={form.password} onChange={set("password")} required
                  className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
              </div>

              <div className="flex gap-2">
                <select value={form.phoneCountryCode} onChange={set("phoneCountryCode")}
                  className="border border-gray-300 rounded-xl px-2 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-400">
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <div className="relative flex-1">
                  <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input type="tel" placeholder="Teléfono (opcional)" value={form.phone} onChange={set("phone")}
                    className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
                </div>
              </div>

              <button type="button" onClick={() => setStep(2)}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition">
                Continuar <ChevronRight size={16} />
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Datos de la empresa</p>

              <div className="relative">
                <Building2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input type="text" placeholder="Nombre de la empresa *" value={form.companyName} onChange={set("companyName")} required
                  className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
              </div>

              <div className="relative">
                <FileText size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input type="text" placeholder="NIT (opcional)" value={form.companyNit} onChange={set("companyNit")}
                  className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="Ciudad" value={form.companyCity} onChange={set("companyCity")}
                  className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
                <input type="tel" placeholder="Teléfono empresa" value={form.companyPhone} onChange={set("companyPhone")}
                  className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
              </div>

              <div className="relative">
                <MapPin size={15} className="absolute left-3.5 top-3.5 text-gray-400 pointer-events-none" />
                <input type="text" placeholder="Dirección (opcional)" value={form.companyAddress} onChange={set("companyAddress")}
                  className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm" />
              </div>

              <textarea placeholder="Descripción (opcional)" value={form.companyDescription} onChange={set("companyDescription")} rows={2}
                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none text-sm resize-none" />

              <div className="flex gap-2">
                <button type="button" onClick={() => setStep(1)}
                  className="flex items-center gap-1 px-4 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
                  <ChevronLeft size={15} />Atrás
                </button>
                <button type="submit" disabled={loading}
                  className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition disabled:opacity-60">
                  {loading ? "Registrando..." : "Crear empresa de domicilio"}
                </button>
              </div>
            </motion.div>
          )}
        </form>

        <p className="text-center text-xs text-gray-500 mt-4">
          ¿Ya tienes cuenta?{" "}
          <span onClick={() => nav("/login")} className="text-indigo-600 hover:underline cursor-pointer font-semibold">
            Inicia sesión
          </span>
        </p>
      </motion.div>
    </div>
  );
}
